import React, { PropsWithChildren, ReactNode, useRef, useState } from 'react';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import {
  getUniqueInitialLetters,
  getUniqueInitialLettersData,
} from '@apps/utils/initial-letters';
import Box, { BoxProps } from '@mui/material/Box';
import Stack, { StackProps } from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainList, { MainListHandle } from './MainItem';
import SubMenu from './SubMenu';

export type AlphabetListWithLettersProps<T = never> = {
  title?: string;
  items: T[];
  valueKey: keyof T;
  labelKey: keyof T;
  initialBrandSlug: string;
  imageKey?: keyof T;
  renderItem?: (data: T) => ReactNode;
  fullWidth?: boolean;
  onItemSelect?: (item: { slug?: string; name?: string }) => void;
  containerProps?: StackProps;
  alphabetContainerProps?: BoxProps;
  itemRight?: boolean;
  webView?: boolean;
  basePath?: string;
};

const AlphabetListWithLetters = <T,>({
  title,
  items,
  renderItem,
  onItemSelect,
  valueKey,
  labelKey,
  imageKey,
  initialBrandSlug,
  fullWidth,
  containerProps,
  alphabetContainerProps,
  itemRight = true,
  webView,
  basePath,
  children,
}: PropsWithChildren<AlphabetListWithLettersProps<T>>) => {
  const mainListRef = useRef<MainListHandle>(null);
  const itemName = items?.map((val) => val[valueKey]) as string[];
  const dataList = getUniqueInitialLettersData(items, valueKey);
  const initialLetters = getUniqueInitialLetters(itemName as string[]);
  const [currentBrand, setCurrentBrand] = useState<string>(
    initialBrandSlug ?? ''
  );
  const { clickAction } = usePostMsgContext();

  const handleClick = (event: React.MouseEvent<HTMLElement>, key: string) => {
    event.preventDefault();
    if (mainListRef.current) {
      mainListRef.current.scrollToSection(key);
    }
  };

  const clickBrand = (brandSlug: string, brandName: string) => {
    if (webView) {
      clickAction(`${basePath}/${brandSlug}`, false, {
        title: brandName,
        params: {
          brand: brandSlug,
        },
      });
      return;
    }
    // Handle brand click
    setCurrentBrand(brandSlug);
    onItemSelect?.({ slug: brandSlug, name: brandName });
  };

  // =========== VIEW
  return (
    <Stack sx={{ gap: 5 }}>
      {title && (
        <Stack sx={{ borderBottom: '2px solid #1E242F', flexDirection: 'row' }}>
          <Typography
            sx={{
              background: '#1E242F',
              color: 'white',
              fontWeight: '700',
              fontSize: '.875rem',
              padding: '4px 20px',
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: '16px',
              height: '100%',
              background:
                'linear-gradient(37deg, #1E242F 70%, rgba(0, 0, 0, 0) 73%)',
            }}
          />
        </Stack>
      )}
      <Stack
        {...containerProps}
        sx={{
          flexDirection: 'row',
          background: '#F7F7F7',
          position: 'relative',
          justifyContent: itemRight ? 'flex-start' : 'flex-end',
          width: fullWidth ? '100%' : '300px',
          ...containerProps?.sx,
        }}
      >
        <SubMenu
          list={initialLetters}
          handleClick={handleClick}
          mainListRef={mainListRef}
          itemRight={itemRight}
          containerProps={alphabetContainerProps}
        />
        <MainList
          ref={mainListRef}
          list={dataList}
          labelKey={labelKey}
          valueKey={valueKey}
          imageKey={imageKey}
          current_brand={currentBrand as unknown as string}
          clickBrand={(item) => clickBrand(item?.[valueKey], item?.[labelKey])}
          renderItem={renderItem}
          itemRight={itemRight}
        />
      </Stack>
      {children}
    </Stack>
  );
};

export default AlphabetListWithLetters;
